import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Section } from '@dized/ui';
import { ChartJsView } from './ChartJsView';
import { getLocaleName } from '../LocaleTable/LocaleList';

const ResponsiveTitle = styled.h3`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  margin: 0 0 20px 0;
  word-break: break-word;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    margin: 0 0 15px 0;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

export const LanguageDistribution = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Section>
        <ResponsiveTitle>Language Distribution</ResponsiveTitle>
        <p>No language distribution data available</p>
      </Section>
    );
  }

  // Process the array of project language data from the API
  // Each element in the array represents the same project in a different language
  const totalSessions = data.reduce((sum, item) => sum + (item.session_count || 0), 0);
  
  // Group by locale to handle potential duplicates and sum their session counts.
  // Normally this should not happen, but the way API generates data from upstream sources, it can happen.
  const localeMap = new Map();
  
  data.forEach(item => {
    const locale = item.locale;
    const sessionCount = item.session_count || 0;
    
    if (localeMap.has(locale)) {
      // Add to existing locale count
      const existing = localeMap.get(locale);
      existing.count += sessionCount;
    } else {
      // Create new locale entry
      localeMap.set(locale, {
        locale: locale,
        label: getLocaleName(locale) || locale,
        count: sessionCount
      });
    }
  });
  
  // Convert map to array and calculate percentages as numbers
  const localeData = Array.from(localeMap.values()).map(item => ({
    ...item,
    percentage: totalSessions > 0 ? parseFloat(((item.count / totalSessions) * 100).toFixed(1)) : 0
  }));

  return (
    <Section>
      <ResponsiveTitle>Language Distribution</ResponsiveTitle>
      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '20px' }}>
        Language distribution across {localeData.length} locale{localeData.length !== 1 ? 's' : ''} for {totalSessions} total sessions
      </p>
      
      <ChartContainer>
        <ChartJsView data={localeData} />
      </ChartContainer>
    </Section>
  );
};

LanguageDistribution.propTypes = {
  data: PropTypes.array
};

LanguageDistribution.displayName = 'LanguageDistribution';
